import React from 'react';
import { AlertCircle, FileText, Users } from 'lucide-react';
import { useReadyTranslation } from '../hooks/useReadyTranslation';
import {
  Ventana, Casilla, BloqueEsqueleto, BOTON_VENTANA_PRINCIPAL, BOTON_VENTANA_SECUNDARIO, APRETADO,
} from './alumno';
import { nombreConCortes } from './panel/anatomia';

/* pantallas-alumno (C): LA VISTA DE LA LISTA DE TEMAS, CON LA VENTANA DEL PANEL (CE4 de la parte 1).

   ALTO_VISOR: la lista de temas se desplaza por dentro con alto de 380 px, en los cuatro estados
   (cargando, no cargo, sin temas, con temas), asi que la ventana no brinca al pasar de uno a otro
   (regla 4). Lapida de #420 y #461: aqui vivia la cuenta de 383 y despues de 485 px, con la hoja
   de Radix del telefono y el chasis de modal de la casa; el diseno de la parte 1 (CE4) la cambia
   por la ventana H del Panel (480; hoja desde abajo en el celular) con la lista de 380.
   ALTO_FILA_MINIMO: el renglon de un tema mide al menos 48 (relleno 12, radio 12). */
export const ALTO_VISOR = 380;
export const ALTO_FILA_MINIMO = 48;

/**
 * TopicPickerModal: EL SELECTOR DE TEMAS DE UNA SESION (TANDA 29, #272).
 *
 * Una lista de casillas agrupada por documento. Cero palomeadas = TODOS los temas, que es la sesion
 * de siempre: no existe el aviso "marca al menos un tema".
 *
 * pantallas-alumno (C, CE4-a a CE4-f): la ventana es la del Panel (`components/alumno/Ventana`,
 * 480 en computadora y tablet, hoja desde abajo en el celular, con la X que hoy tiene la hoja, Esc
 * y el velo cierran). Cerrar con la X, con el velo o con "Done" hacen LO MISMO: aplicar lo marcado.
 *
 * Los cuatro estados del cuerpo, sin solaparse (#413): cargando (esqueleto de 5 renglones), no cargo
 * (texto rojo con su icono), sin temas para el modo (al centro) y la lista. La frase de ayuda solo
 * acompana a la lista; en los otros tres su renglon queda reservado en blanco.
 *
 * P6: el encabezado de cada documento lleva su icono (personas para una publicacion) y el nombre
 * con "...": dos renglones en el celular, uno desde 768, completo al pasar el mouse (`title`).
 */
export default function TopicPickerModal({
  open,
  onClose,
  mode,
  documents,
  loading,
  cargaFallida,
  selected,
  onToggle,
  onClear,
}) {
  const { t, ready } = useReadyTranslation(['study', 'common']);

  if (!open || !ready) return null;

  // El conteo que se pinta es el DEL MODO ACTUAL, y un tema con 0 en ese modo no se muestra.
  const esFlashcards = mode === 'flashcards';
  const conteoDe = (tema) => (esFlashcards ? tema.flashcard_count : tema.question_count);
  const etiquetaDeConteo = (n) => t(
    esFlashcards ? 'study:center.topics.flashcards' : 'study:center.topics.questions',
    { count: n },
  );

  // t7-1c (#478): una entrada de CARD se identifica por su card (prefijo `pub:`); un documento, por
  // su documento, como siempre.
  const grupos = (documents || [])
    .map((doc) => ({
      ...doc,
      idDeEntrada: doc.publicacion_id ? `pub:${doc.publicacion_id}` : doc.document_id,
      topics: (doc.topics || []).filter((tema) => conteoDe(tema) > 0),
    }))
    .filter((doc) => doc.topics.length > 0);

  const vacia = grupos.length === 0;
  const mostrarAyuda = !loading && !cargaFallida && !vacia;

  const cuerpo = (
    <>
      <p
        className="text-[13px] leading-[18px] text-[#9A9A9A]"
        aria-hidden={mostrarAyuda ? undefined : true}
        data-testid={mostrarAyuda ? 'topic-picker-hint' : 'topic-picker-hint-reserva'}
      >
        {mostrarAyuda ? t('study:center.topics.modal_hint') : '\u00A0'}
      </p>
      <div
        className="mt-3 h-[380px] overflow-y-auto overscroll-contain pr-1 [scrollbar-width:thin] [scrollbar-color:#3A3A3A_transparent]"
        data-testid="topic-picker-viewport"
      >
        {loading ? (
          <div className="flex flex-col gap-2" data-testid="topic-picker-skeleton">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="min-h-[48px] p-3 rounded-xl border border-[#1F1F1F] bg-[#101010] flex items-center gap-3">
                <BloqueEsqueleto className="w-[22px] h-[22px] rounded-[7px] shrink-0" />
                <BloqueEsqueleto className="h-3.5 flex-1" />
                <BloqueEsqueleto className="h-3 w-16 shrink-0" />
              </div>
            ))}
          </div>
        ) : cargaFallida ? (
          <p className="flex items-start gap-2 text-[13px] leading-[18px] text-[#F87171]" data-testid="topic-picker-empty-failed">
            <AlertCircle className="w-4 h-4 shrink-0 mt-px" aria-hidden="true" />
            <span className="min-w-0 break-words">{t('study:center.topics.empty_failed')}</span>
          </p>
        ) : vacia ? (
          <div className="h-full flex items-center justify-center">
            <p className="text-[14px] leading-5 text-[#9A9A9A] text-center" data-testid="topic-picker-empty-no-topics">
              {t('study:center.topics.empty_no_topics_mode')}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4" data-testid="topic-picker-list">
            {grupos.map((doc) => {
              const Icono = doc.publicacion_id ? Users : FileText;
              return (
                <div key={doc.idDeEntrada}>
                  <p className="flex items-start gap-1.5 mb-2 text-[12.5px] leading-[17px] font-semibold text-[#8A8A8A]"
                    data-testid={`topic-picker-doc-${doc.idDeEntrada}`}>
                    <Icono className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden="true" />
                    <span title={doc.filename} className="min-w-0 flex-1 break-words line-clamp-2 md:line-clamp-1 md:break-all">
                      {nombreConCortes(doc.filename)}
                    </span>
                  </p>
                  <div className="flex flex-col gap-2">
                    {doc.topics.map((tema) => {
                      const marcado = selected.includes(tema.name);
                      return (
                        <button
                          key={tema.name}
                          type="button"
                          role="checkbox"
                          aria-checked={marcado}
                          onClick={() => onToggle(tema.name)}
                          data-testid={`topic-card-${doc.idDeEntrada}-${tema.name}`}
                          className={`w-full min-h-[48px] p-3 flex items-center gap-3 rounded-xl border text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${marcado ? 'bg-[#17100B] border-[#3A2414]' : 'bg-[#101010] border-[#1F1F1F] hoverable:border-[#2C2C2C]'} ${APRETADO}`}
                        >
                          <Casilla marcada={marcado} />
                          <span className="flex-1 min-w-0 text-[14.5px] leading-5 text-[#F4F4F3] break-words">{tema.name}</span>
                          <span className="shrink-0 text-[12.5px] text-[#8A8A8A] tabular-nums whitespace-nowrap"
                            data-testid={`topic-count-${doc.idDeEntrada}-${tema.name}`}>
                            {etiquetaDeConteo(conteoDe(tema))}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );

  return (
    <Ventana
      titulo={t('study:center.topics.modal_title')}
      etiquetaId="topic-picker-title"
      ancho={480}
      etiquetaCerrar={t('common:close')}
      alCerrar={onClose}
      testid="topic-picker-modal"
      botones={(
        <>
          <button type="button" onClick={onClear} className={BOTON_VENTANA_SECUNDARIO} data-testid="topic-picker-clear-btn">
            {t('study:center.topics.clear_btn')}
          </button>
          <button type="button" onClick={onClose} className={BOTON_VENTANA_PRINCIPAL} data-testid="topic-picker-done-btn">
            {t('study:center.topics.done_btn')}
          </button>
        </>
      )}
    >
      {cuerpo}
    </Ventana>
  );
}
